import React, { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  Avatar,
  Autocomplete,
  Alert,
  IconButton,
  LinearProgress,
  FormHelperText,
  Backdrop,
} from "@mui/material";
import {
  CloudUploadOutlined,
  Visibility,
  Delete,
  InsertDriveFile,
} from "@mui/icons-material";
import NameLogo from "src/@core/components/icons/Name-Logo";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints, BUSINESS_DOCUMENTS_URL } from "src/network/endpoints";
import { useSelector } from "react-redux";
import * as Yup from "yup";


const isPreviewableFile = (fileType) => {
  const previewableTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
  ];
  return previewableTypes.includes(fileType);
};

const FileViewPopup = ({ onClose, open, filePath, fileType }) => {
  const handleClose = () => {
    onClose();
  };

  if (!isPreviewableFile(fileType)) {
    return null;
  }

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
      onClick={handleClose}
    >
      <iframe
        id="receipt"
        src={filePath}
        style={{ width: "80%", height: "80%", display: "block" }}
        title="document preview"
      />
    </Backdrop>
  );
};

// Validation Schema
const validationSchema = Yup.object({
  businessLicenseRegistration: Yup.string()
    .required("Please enter business license/registration number")
    .trim(),
  country: Yup.object()
    .nullable()
    .required("Please select a country"),
  documents: Yup.array()
    .min(1, "Please upload at least one document")
    .of(
      Yup.object({
        name: Yup.string()
          .required("Document name is required")
          .trim(),
        file: Yup.mixed()
          .nullable()
          .test("file-required", "Please upload a document", function(value) {
            const parentValues = this.parent;
            
            // If it's an existing document with a path, file is not required
            if (parentValues?.isExisting && parentValues?.path) {
              return true;
            }
            // For new documents, file is required
            return value !== null && value !== undefined;
          })
      }).test('document-validity', 'Document must have either a file or be an existing document', function(doc) {
        // Document is valid if it's either:
        // 1. An existing document with a path, OR
        // 2. A new document with a file
        return (doc.isExisting && doc.path) || (!doc.isExisting && doc.file);
      })
    )
});

export default function BusinessVerification({ businessData, onSuccess }) {
  console.log("businessData", businessData);
  const {
    businessDocuments = [],
    businessLicenseRegistration,
    country: businessCountry,
    isApprove,
    isDocumentVerified,
  } = businessData?.business || {};

  // Check if business is verified by admin
  const isVerified = isApprove === 1 && isDocumentVerified === 1;

  const [license, setLicense] = React.useState("");
  const [country, setCountry] = React.useState(null);
  const [documents, setDocuments] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [documentLoaders, setDocumentLoaders] = React.useState({});
  const [openFilePopup, setOpenFilePopup] = React.useState(false);
  const [selectedFilePath, setSelectedFilePath] = React.useState("");
  const [selectedFileType, setSelectedFileType] = React.useState("");

  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const queryClient = useQueryClient();

  const { data: countryList } = useQuery(
    ["countryList"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.country, {
        signal,
      });
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  // Helper function to get file type from path
  const getFileTypeFromPath = (path) => {
    const extension = path?.split('.').pop()?.toLowerCase();
    const typeMapping = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    return typeMapping[extension] || 'application/octet-stream';
  };

  // Initialize form data when businessData or countryList changes
  useEffect(() => {
    if (businessData && countryList?.country) {
      // Set license
      setLicense(businessLicenseRegistration || "");
      
      // Set country
      let initialCountry = null;
      if (businessCountry) {
        initialCountry = countryList.country.find(c => c.name === businessCountry) || null;
      }
      setCountry(initialCountry);

      // Set documents
      if (businessDocuments && businessDocuments.length > 0) {
        const initialDocuments = businessDocuments.map((doc, index) => ({
          name: doc.name || "",
          file: null, // Existing documents don't need file object
          id: doc._id || `existing_${index}`,
          filePath: `${BUSINESS_DOCUMENTS_URL}${doc.path}`, // Add AWS URL prefix for existing documents
          fileType: getFileTypeFromPath(doc.path),
          path: doc.path, // Keep original path for API
          isExisting: true, // Flag to identify existing documents
          _id: doc._id
        }));
        setDocuments(initialDocuments);
      } else {
        // Initialize with one document for new entries
        setDocuments([{
          name: "",
          file: null,
          id: Date.now(),
          filePath: "",
          fileType: "",
          isExisting: false
        }]);
      }
    } else if (!businessData) {
      // Initialize with default values for new entries
      setLicense("");
      setCountry(null);
      setDocuments([{
        name: "",
        file: null,
        id: Date.now(),
        filePath: "",
        fileType: "",
        isExisting: false
      }]);
    }
  }, [businessData, countryList]);

  // Mutation for uploading business documents
  const uploadBusinessDocumentMutation = useMutation(
    async (data) => {
      const formData = new FormData();

      // Append files and document names for new/updated documents
      data.documents.forEach((doc, index) => {
        // Only append files for new documents or when file is updated
        if (doc.file && !doc.isExisting) {
          formData.append(`files`, doc.file);
        }
        
        formData.append(`documentNames[${index}]`, doc.name);
        
        // If it's an existing document, append its ID and path
        if (doc.isExisting && doc._id) {
          formData.append(`existingDocuments[${index}][id]`, doc._id);
          formData.append(`existingDocuments[${index}][name]`, doc.name);
          if (doc.path) {
            formData.append(`existingDocuments[${index}][path]`, doc.path);
          }
        }
      });

      formData.append("country", data.country.name);
      formData.append("businessLicenseRegistration", data.businessLicenseRegistration);
      formData.append("businessId", data.businessId);

      const response = await axiosInstance.patch(
        ApiEndPoints.BUSINESS.uploadDocument + "/" + selectedBusinessId,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        console.log("Documents uploaded  :", data);
        // Invalidate business location query so UI refetches and shows new documents / status
        queryClient.invalidateQueries(["businessLocationData", selectedBusinessId]);
        if (onSuccess) {
          onSuccess();
        }
        
        // Only reset if it was a new entry (no existing business data)
        if (!businessData || (!businessLicenseRegistration && businessDocuments.length === 0)) {
          setLicense("");
          setCountry(null);
          setDocuments([{
            name: "",
            file: null,
            id: Date.now(),
            filePath: "",
            fileType: "",
            isExisting: false
          }]);
          setDocumentLoaders({});
          setErrors({});

          // Clean up file URLs for new documents
          documents.forEach((doc) => {
            if (doc.filePath && doc.filePath.startsWith('blob:')) {
              URL.revokeObjectURL(doc.filePath);
            }
          });
        }
      },
      onError: (error) => {
        console.error("Error uploading documents:", error);
      },
    }
  );

  // Yup validation function
  const validateForm = async () => {
    try {
      const formData = {
        businessLicenseRegistration: license,
        country: country,
        documents: documents
      };

      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationErrors) {
      const newErrors = {};
      
      validationErrors.inner.forEach((error) => {
        const path = error.path;
        
        if (path === 'businessLicenseRegistration') {
          newErrors.license = error.message;
        } else if (path === 'country') {
          newErrors.country = error.message;
        } else if (path === 'documents') {
          newErrors.documents = error.message;
        } else if (path.includes('documents[') && path.includes('].name')) {
          const index = path.match(/documents\[(\d+)\]/)[1];
          newErrors[`docName_${index}`] = error.message;
        } else if (path.includes('documents[') && path.includes('].file')) {
          const index = path.match(/documents\[(\d+)\]/)[1];
          newErrors[`docFile_${index}`] = error.message;
        } else if (path.includes('documents[') && !path.includes('].')) {
          // Handle document-level validation errors
          const index = path.match(/documents\[(\d+)\]/)[1];
          newErrors[`docFile_${index}`] = error.message;
        }
      });
      
      setErrors(newErrors);
      return false;
    }
  };

  const addNewDocument = () => {
    // Prevent adding documents if business is verified
    if (isVerified) return;
    
    setDocuments((prev) => [
      ...prev,
      {
        name: "",
        file: null,
        id: Date.now(),
        filePath: "",
        fileType: "",
        isExisting: false
      },
    ]);
  };

  const updateDocument = (index, field, value) => {
    setDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, [field]: value } : doc))
    );

    // Clear specific field errors when user starts typing/selecting
    const errorKey = field === "name" ? `docName_${index}` : `docFile_${index}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: "",
      }));
    }
  };

  const handleFileUpload = async (index, event) => {
    // Prevent file upload if business is verified
    if (isVerified) return;
    
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [`docFile_${index}`]: "Only PDF, JPG, JPEG, PNG, DOC, DOCX files are allowed",
      }));
      return;
    }

    setDocumentLoaders((prev) => ({ ...prev, [index]: true }));

    setTimeout(() => {
      const filePath = URL.createObjectURL(file);
      updateDocument(index, "file", file);
      updateDocument(index, "filePath", filePath);
      updateDocument(index, "fileType", file.type);
      updateDocument(index, "isExisting", false); // Mark as new document
      updateDocument(index, "path", ""); // Clear existing path since it's a new file

      if (!documents[index]?.name) {
        updateDocument(index, "name", file.name.split(".")[0]);
      }

      setDocumentLoaders((prev) => ({ ...prev, [index]: false }));
    }, 1000);
  };

  const removeDocument = (index) => {
    const document = documents[index];
    
    // Only revoke URL if it's a blob URL (not AWS URL)
    if (document?.filePath && document.filePath.startsWith('blob:')) {
      URL.revokeObjectURL(document.filePath);
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`docName_${index}`];
      delete newErrors[`docFile_${index}`];
      return newErrors;
    });

    setDocumentLoaders((prev) => {
      const newLoaders = { ...prev };
      delete newLoaders[index];
      return newLoaders;
    });
  };

  const handleViewDocument = (filePath, fileType) => {
    if (isPreviewableFile(fileType)) {
      setSelectedFilePath(filePath);
      setSelectedFileType(fileType);
      setOpenFilePopup(true);
    } else {
      // For non-previewable files, download them
      const link = document.createElement('a');
      link.href = filePath;
      link.target = '_blank';
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCloseFilePopup = () => {
    setOpenFilePopup(false);
    setSelectedFilePath("");
  };

  const handleCountryChange = (event, newValue) => {
    setCountry(newValue);
    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: "" }));
    }
  };

  const handleLicenseChange = (e) => {
    setLicense(e.target.value);
    if (errors.license) {
      setErrors((prev) => ({ ...prev, license: "" }));
    }
  };

  const handleUpload = async () => {
    // Prevent upload if business is verified
    if (isVerified) return;
    
    const isValid = await validateForm();
    if (!isValid) return;

    uploadBusinessDocumentMutation.mutate({
      documents,
      country: country,
      businessLicenseRegistration: license,
      businessId: selectedBusinessId,
    });
  };

  return (
    <Box
      sx={{
        width: 380,
        mx: "auto",
        py: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 4,
      }}
    >
      {/* Loading indicator */}
      {uploadBusinessDocumentMutation.isLoading && (
        <LinearProgress
          sx={{
            width: "100%",
            mb: 2,
            "& .MuiLinearProgress-bar": { backgroundColor: "#6200EE" },
          }}
        />
      )}

      {/* Logo Avatar */}
      <Avatar
        sx={{
          width: { xs: 80, sm: 90 },
          height: { xs: 80, sm: 90 },
          bgcolor: "#fff",
          border: "4px solid #6200EE",
          mx: "auto",
          mb: 2,
          p: { xs: 4, sm: 5 },
          boxShadow: "0 8px 25px rgba(162, 89, 255, 0.25)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <NameLogo />
      </Avatar>

      {/* Brand Title */}
      <Typography
        variant="h3"
        align="center"
        fontWeight="bold"
        letterSpacing={1}
        sx={{
          mb: 0.5,
          textTransform: "lowercase",
          color: "#000",
          fontFamily: "Inter, Arial, sans-serif",
          fontSize: { xs: "2rem", sm: "2.5rem" },
          position: "relative",
          zIndex: 1,
        }}
      >
        beɘ
        <Box component="span" sx={{ color: "#6200EE" }}>
          m
        </Box>
        z
      </Typography>

      {/* Title */}
      <Typography
        variant="h6"
        align="center"
        sx={{ color: "#6200EE", fontWeight: 700, mb: 3 }}
      >
        business verification
      </Typography>

      {/* Error/Success Messages */}
      {uploadBusinessDocumentMutation.isError && (
        <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
          {uploadBusinessDocumentMutation.error?.response?.data?.message ||
            uploadBusinessDocumentMutation.error?.message ||
            "Failed to upload documents"}
        </Alert>
      )}

      {businessDocuments?.length > 0 && isApprove === 0 && isDocumentVerified === 0 && (
        <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
          document pending to approve
        </Alert>
      )}

      {/* Admin Verified Message */}
      {isVerified && (
        <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
          admin verified - documents have been approved
        </Alert>
      )}

      {/* License input */}
      <Typography sx={{ fontWeight: 500, mb: 0.5, alignSelf: "flex-start" }}>
        upload incorporation/registration/license documents
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="enter business registration number"
        value={license}
        onChange={handleLicenseChange}
        disabled={uploadBusinessDocumentMutation.isLoading || isVerified}
        error={!!errors.license}
        helperText={errors.license}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        }}
        InputProps={{
          sx: { fontSize: "1.07em" },
        }}
      />

      {/* Country select with search */}
      <Typography sx={{ fontWeight: 500, mb: 0.5, alignSelf: "flex-start" }}>
        select country
      </Typography>
      <Autocomplete
        fullWidth
        options={countryList?.country || []}
        getOptionLabel={(option) => option.name || ""}
        value={country}
        onChange={handleCountryChange}
        disabled={uploadBusinessDocumentMutation.isLoading || isVerified}
        isOptionEqualToValue={(option, value) => option._id === value?._id}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="search and select country"
            error={!!errors.country}
            helperText={errors.country}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "1.07em",
                bgcolor: "#fff",
              },
            }}
          />
        )}
        sx={{ mb: 3 }}
        noOptionsText="No countries found"
        filterOptions={(options, { inputValue }) =>
          options.filter((option) =>
            option.name.toLowerCase().includes(inputValue.toLowerCase())
          )
        }
      />

      {/* Documents Upload Section */}
      <Typography sx={{ fontWeight: 500, mb: 1.5, alignSelf: "flex-start" }}>
        upload incorporation/registration/license documents
      </Typography>

      {errors.documents && (
        <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
          {errors.documents}
        </Alert>
      )}

      {/* Document Upload Cards */}
      {documents.map((document, index) => (
        <Box key={document.id} sx={{ width: "100%", mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              fontSize: "0.875rem",
              mb: 1,
              fontWeight: 500,
            }}
          >
            {index === 0 ? "primary document" : `document ${index + 1}`}
          </Typography>

          <TextField
            fullWidth
            value={document.name}
            onChange={(e) => updateDocument(index, "name", e.target.value)}
            error={!!errors[`docName_${index}`]}
            helperText={errors[`docName_${index}`]}
            placeholder={
              index === 0 ? "primary document name" : "document name"
            }
            variant="outlined"
            disabled={uploadBusinessDocumentMutation.isLoading || isVerified}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#f8f9fa",
              },
            }}
          />

          <Box
            sx={{
              border: "2px dashed #d1d3db",
              borderRadius: 2,
              p: 2,
              backgroundColor: "#fafbfc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <InsertDriveFile sx={{ color: "#6200EE", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "#333",
                  fontSize: "0.9em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "150px",
                }}
              >
                {document.file 
                  ? document.file.name 
                  : document.isExisting && document.name
                  ? document.name
                  : "no file selected"
                }
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
              {(document.file || document.filePath) && (
                <>
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleViewDocument(document.filePath, document.fileType)
                    }
                    sx={{ color: "#666" }}
                    title="view document"
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                  {documents.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeDocument(index)}
                      sx={{ color: "#666" }}
                      title="delete document"
                      disabled={uploadBusinessDocumentMutation.isLoading || isVerified}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}

              <Button
                variant="contained"
                component="label"
                size="small"
                disabled={uploadBusinessDocumentMutation.isLoading || isVerified}
                sx={{
                  textTransform: "none",
                  minWidth: "auto",
                  bgcolor: "#6200EE",
                  fontSize: "0.8em",
                  px: 2,
                  "&:hover": { bgcolor: "#761afc" },
                }}
              >
                {document.file || document.isExisting ? "change" : "browse"}
                <input
                  hidden
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={(e) => handleFileUpload(index, e)}
                  type="file"
                  disabled={isVerified}
                />
              </Button>
            </Box>
          </Box>

          {documentLoaders[index] && (
            <LinearProgress
              sx={{
                mt: 1,
                "& .MuiLinearProgress-bar": { backgroundColor: "#6200EE" },
              }}
            />
          )}

          {errors[`docFile_${index}`] && (
            <FormHelperText error sx={{ ml: 0, mt: 1 }}>
              {errors[`docFile_${index}`]}
            </FormHelperText>
          )}

          <Typography
            variant="caption"
            sx={{
              color: "#888",
              fontSize: "0.75rem",
              display: "block",
              mt: 1,
            }}
          >
            supported formats: PDF, JPG, PNG, DOC, DOCX
          </Typography>
        </Box>
      ))}

      {/* Add Another Document Button */}
      {!isVerified && (
      <Button
        variant="outlined"
        onClick={addNewDocument}
        disabled={uploadBusinessDocumentMutation.isLoading}
        sx={{
          borderColor: "#6200EE",
          color: "#fff",
          textTransform: "none",
          borderRadius: 2,
          mb: 3,
          fontWeight: 600,
          "&:hover": {
            borderColor: "#761afc",
            color: "#761afc",
            bgcolor: "rgba(162, 89, 255, 0.04)",
          },
        }}
      >
        + add another document
      </Button>
      )}

      {/* Upload button */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleUpload}
        disabled={uploadBusinessDocumentMutation.isLoading || isVerified}
        sx={{
          bgcolor: "#6200EE",
          color: "#fff",
          borderRadius: 2,
          fontWeight: 700,
          fontSize: "1.15em",
          boxShadow: "none",
          textTransform: "none",
          height: 48,
          mt: 1,
          "&:hover": { bgcolor: "#6200EE" },
        }}
      >
        {uploadBusinessDocumentMutation.isLoading
          ? "uploading..."
          : "upload documents"}
      </Button>

      {/* File Preview Popup */}
      <FileViewPopup
        open={openFilePopup}
        filePath={selectedFilePath}
        fileType={selectedFileType}
        onClose={handleCloseFilePopup}
      />
    </Box>
  );
}